import React, { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Container,
  Divider,
  Grid,
  IconButton,
  Paper,
  Tab,
  Tabs,
  Typography,
} from '@mui/material';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import PersonIcon from '@mui/icons-material/Person';
import { Link } from 'react-router-dom';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { getTicketLotName } from '../../../components/events/lotDisplayUtils';
import { useBrand } from '../../../brands';
import { useBrandRegistrationCopy } from '../../../brands/registrationCopy';

export type RegistrationSuccessEvent = {
  title: string;
  startDate?: string;
  endDate?: string;
  location?: string;
  address?: string;
  city?: string;
  state?: string;
  postRegistrationUrl?: string;
};

export type RegistrationSuccessTicket = {
  name: string;
  price: number;
  salesPhaseName?: string;
  batchName?: string;
};

export type RegistrationSuccessAttendee = {
  registrationId: string;
  participant: {
    name: string;
    email: string;
    phone: string;
  };
  ticket: RegistrationSuccessTicket | null;
  checkInData?: { qrCodeBase64?: string; checkInCode?: string };
};

export type RegistrationSuccessViewProps = {
  event: RegistrationSuccessEvent;
  attendees?: RegistrationSuccessAttendee[];
  totalAmount: number;
  needsPayment: boolean;
  /** Quantidade na inscrição única (quando não é carrinho com várias pessoas). */
  quantity?: number;
  onDownloadTicket: (registrationId: string) => void;
  /** @deprecated use attendees */
  participant?: { name: string; email: string; phone: string };
  /** @deprecated use attendees */
  ticket?: RegistrationSuccessTicket | null;
  /** @deprecated use attendees */
  checkInData?: { qrCodeBase64?: string; checkInCode?: string } | null;
  /** @deprecated use attendees */
  registrationId?: string;
};

function formatDate(dateString?: string): string {
  if (!dateString) return '';
  try {
    return format(new Date(dateString), "dd 'de' MMMM 'de' yyyy 'às' HH:mm", { locale: ptBR });
  } catch {
    return dateString;
  }
}

function firstName(fullName: string): string {
  return fullName.trim().split(/\s+/)[0] || fullName;
}

const RegistrationSuccessView: React.FC<RegistrationSuccessViewProps> = ({
  event,
  attendees: attendeesProp,
  totalAmount,
  needsPayment,
  quantity = 1,
  onDownloadTicket,
  participant: legacyParticipant,
  ticket: legacyTicket,
  checkInData: legacyCheckInData,
  registrationId: legacyRegistrationId,
}) => {
  const attendees =
    attendeesProp && attendeesProp.length > 0
      ? attendeesProp
      : legacyParticipant
        ? [
            {
              registrationId: legacyRegistrationId || '',
              participant: legacyParticipant,
              ticket: legacyTicket ?? null,
              checkInData: legacyCheckInData || undefined,
            },
          ]
        : [];

  const [activeIndex, setActiveIndex] = useState(0);
  const t = useBrandRegistrationCopy();
  const brand = useBrand();
  const showPlatformApp = brand.key !== 'zuadaria';

  if (attendees.length === 0) {
    return (
      <Container maxWidth="md" sx={{ my: 5 }}>
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h6" gutterBottom>
            {t('Inscrição confirmada')}
          </Typography>
          <Typography color="text.secondary">
            Carregando detalhes do ingresso…
          </Typography>
        </Paper>
      </Container>
    );
  }

  const safeIndex = Math.min(activeIndex, attendees.length - 1);
  const multiple = attendees.length > 1;
  const current = attendees[safeIndex] ?? attendees[0];
  const participant = current?.participant;
  const ticket = current?.ticket ?? null;
  const checkInData = current?.checkInData;

  const isWhatsAppLink =
    !!event.postRegistrationUrl && /whatsapp\.com|wa\.me/i.test(event.postRegistrationUrl);

  const goPrev = () => setActiveIndex((i) => Math.max(0, i - 1));
  const goNext = () => setActiveIndex((i) => Math.min(attendees.length - 1, i + 1));

  return (
    <Container maxWidth="md" sx={{ my: 5 }}>
      <Paper elevation={3} sx={{ overflow: 'hidden' }}>
        <Box
          sx={{
            background: 'linear-gradient(135deg, #4CAF50 0%, #45a049 100%)',
            color: 'white',
            py: 4,
            px: 3,
            textAlign: 'center',
          }}
        >
          <Box sx={{ fontSize: '3rem', mb: 1 }}>✓</Box>
          <Typography variant="h4" fontWeight="bold" gutterBottom>
            {t('Inscrição Confirmada!')}
          </Typography>
          <Typography variant="body1">
            {multiple
              ? `${attendees.length} ingressos foram gerados com sucesso`
              : 'Seu ingresso foi gerado com sucesso'}
          </Typography>
        </Box>

        <Box sx={{ p: 4 }}>
          {multiple && (
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1.5 }}>
                Selecione a pessoa para ver o QR Code e baixar o ingresso:
              </Typography>
              <Tabs
                value={safeIndex}
                onChange={(_, value) => setActiveIndex(value)}
                variant="scrollable"
                scrollButtons="auto"
                sx={{
                  mb: 1,
                  '& .MuiTab-root': { textTransform: 'none', minHeight: 48, fontWeight: 500 },
                }}
              >
                {attendees.map((item, index) => (
                  <Tab
                    key={item.registrationId}
                    value={index}
                    label={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Chip
                          size="small"
                          label={index + 1}
                          color={safeIndex === index ? 'primary' : 'default'}
                          sx={{ height: 22, minWidth: 28, fontWeight: 700 }}
                        />
                        <span>{firstName(item.participant.name)}</span>
                      </Box>
                    }
                  />
                ))}
              </Tabs>
            </Box>
          )}

          {checkInData?.qrCodeBase64 && (
            <Box sx={{ textAlign: 'center', mb: 4, p: 3, bgcolor: '#f5f5f5', borderRadius: 2 }}>
              {multiple && (
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 1,
                    mb: 2,
                  }}
                >
                  <IconButton
                    onClick={goPrev}
                    disabled={safeIndex === 0}
                    aria-label="Ingresso anterior"
                    size="small"
                  >
                    <ChevronLeftIcon />
                  </IconButton>
                  <Typography variant="body2" color="text.secondary" fontWeight={600}>
                    Ingresso {safeIndex + 1} de {attendees.length}
                    {participant?.name ? ` — ${participant.name}` : ''}
                  </Typography>
                  <IconButton
                    onClick={goNext}
                    disabled={safeIndex >= attendees.length - 1}
                    aria-label="Próximo ingresso"
                    size="small"
                  >
                    <ChevronRightIcon />
                  </IconButton>
                </Box>
              )}
              <Typography variant="h6" fontWeight="bold" gutterBottom>
                Apresente este QR Code no evento:
              </Typography>
              <Box sx={{ my: 2 }}>
                <img
                  src={checkInData.qrCodeBase64}
                  alt={`QR Code de ${participant?.name || 'check-in'}`}
                  style={{ width: 200, height: 200, border: '3px solid #4CAF50', borderRadius: '8px' }}
                />
              </Box>
              <Typography variant="body2" color="text.secondary">
                Código de check-in: <strong>{checkInData.checkInCode}</strong>
              </Typography>
            </Box>
          )}

          {event.postRegistrationUrl && (
            <Box sx={{ mb: 4 }}>
              {isWhatsAppLink ? (
                <Paper
                  elevation={2}
                  sx={{
                    p: 3,
                    borderRadius: 2,
                    bgcolor: '#e7f9f0',
                    border: '1px solid #25D36633',
                    textAlign: 'center',
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      gap: 1,
                      mb: 2,
                    }}
                  >
                    <Box
                      sx={{
                        width: 64,
                        height: 64,
                        borderRadius: '50%',
                        bgcolor: '#25D366',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
                      }}
                    >
                      <WhatsAppIcon sx={{ fontSize: 36, color: '#ffffff' }} />
                    </Box>
                    <Typography variant="h6" fontWeight="bold">
                      Entre no grupo oficial do evento
                    </Typography>
                  </Box>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Clique no botão abaixo para acessar o grupo do evento no WhatsApp e receber avisos
                    importantes, comunicados e novidades diretamente da organização.
                  </Typography>
                  <Button
                    variant="contained"
                    href={event.postRegistrationUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    size="large"
                    sx={{
                      minWidth: 260,
                      bgcolor: '#25D366',
                      color: '#ffffff',
                      fontWeight: 'bold',
                      '&:hover': { bgcolor: '#1ebe5d' },
                    }}
                  >
                    Entrar no grupo do evento
                  </Button>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5 }}>
                    Você será redirecionado para o WhatsApp.
                  </Typography>
                </Paper>
              ) : (
                <Box>
                  <Typography
                    variant="h6"
                    fontWeight="bold"
                    gutterBottom
                    sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                  >
                    🔗 Próximo passo indicado pelo organizador
                  </Typography>
                  <Divider sx={{ mb: 2 }} />
                  <Alert severity="info" sx={{ mb: 2 }}>
                    <Typography variant="body2">
                      {t('O organizador disponibilizou um link com informações ou ações importantes após sua inscrição.')}
                    </Typography>
                  </Alert>
                  <Button
                    variant="contained"
                    color="primary"
                    href={event.postRegistrationUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{ minWidth: 220 }}
                  >
                    Abrir link do organizador
                  </Button>
                </Box>
              )}
            </Box>
          )}

          <Box sx={{ mb: 4 }}>
            <Typography
              variant="h6"
              fontWeight="bold"
              gutterBottom
              sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
            >
              <PersonIcon fontSize="small" color="primary" />
              Informações do Participante
              {multiple && (
                <Chip size="small" label={`${safeIndex + 1}/${attendees.length}`} sx={{ ml: 0.5 }} />
              )}
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Typography variant="body2" color="text.secondary">
                  Nome
                </Typography>
                <Typography variant="body1" fontWeight="medium">
                  {participant?.name}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="text.secondary">
                  Email
                </Typography>
                <Typography variant="body1" fontWeight="medium">
                  {participant?.email}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="text.secondary">
                  Telefone
                </Typography>
                <Typography variant="body1" fontWeight="medium">
                  {participant?.phone}
                </Typography>
              </Grid>
            </Grid>
          </Box>

          <Box sx={{ mb: 4 }}>
            <Typography
              variant="h6"
              fontWeight="bold"
              gutterBottom
              sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
            >
              📅 Informações do Evento
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Typography variant="body2" color="text.secondary">
                  Nome do Evento
                </Typography>
                <Typography variant="body1" fontWeight="medium">
                  {event.title}
                </Typography>
              </Grid>
              {event.startDate && (
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">
                    Data de Início
                  </Typography>
                  <Typography variant="body1">{formatDate(event.startDate)}</Typography>
                </Grid>
              )}
              {event.endDate && (
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">
                    Data de Término
                  </Typography>
                  <Typography variant="body1">{formatDate(event.endDate)}</Typography>
                </Grid>
              )}
              {event.location && (
                <Grid item xs={12}>
                  <Typography variant="body2" color="text.secondary">
                    Local
                  </Typography>
                  <Typography variant="body1">{event.location}</Typography>
                  {(event.address || event.city) && (
                    <Typography variant="body2" color="text.secondary">
                      {[event.address, event.city, event.state].filter(Boolean).join(', ')}
                    </Typography>
                  )}
                </Grid>
              )}
            </Grid>
          </Box>

          <Box sx={{ mb: 4 }}>
            <Typography
              variant="h6"
              fontWeight="bold"
              gutterBottom
              sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
            >
              🎫 Detalhes do Ingresso
              {multiple && (
                <Typography component="span" variant="body2" color="text.secondary" fontWeight={400}>
                  (desta pessoa)
                </Typography>
              )}
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <Paper variant="outlined" sx={{ p: 2 }}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">
                    Tipo de Ingresso
                  </Typography>
                  <Typography variant="body1" fontWeight="medium">
                    {ticket?.name || '—'}
                  </Typography>
                </Grid>
                {ticket && getTicketLotName(ticket) && (
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color="text.secondary">
                      Lote
                    </Typography>
                    <Typography variant="body1" fontWeight="medium">
                      {getTicketLotName(ticket)}
                    </Typography>
                  </Grid>
                )}
                {!multiple && (
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color="text.secondary">
                      Quantidade
                    </Typography>
                    <Typography variant="body1" fontWeight="medium">
                      {quantity}
                    </Typography>
                  </Grid>
                )}
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">
                    Valor Unitário
                  </Typography>
                  <Typography variant="body1" fontWeight="medium">
                    {ticket?.price && ticket.price > 0 ? `R$ ${ticket.price.toFixed(2)}` : 'Gratuito'}
                  </Typography>
                </Grid>
                {multiple ? (
                  <Grid item xs={12}>
                    <Typography variant="body2" color="text.secondary">
                      Total do pedido ({attendees.length} ingressos)
                    </Typography>
                    <Typography variant="h6" color="primary" fontWeight="bold">
                      R$ {totalAmount.toFixed(2)}
                    </Typography>
                  </Grid>
                ) : (
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color="text.secondary">
                      Valor Total
                    </Typography>
                    <Typography variant="h6" color="primary" fontWeight="bold">
                      R$ {totalAmount.toFixed(2)}
                    </Typography>
                  </Grid>
                )}
              </Grid>
            </Paper>
          </Box>

          {needsPayment && (
            <Alert severity="info" sx={{ mb: 3 }}>
              <Typography variant="body2" fontWeight="medium">
                💳 Detalhes do pagamento foram enviados para o seu e-mail.
              </Typography>
            </Alert>
          )}

          <Alert severity="success" icon={<Box sx={{ fontSize: '1.5rem' }}>📧</Box>} sx={{ mb: 3 }}>
            <Typography variant="body2" fontWeight="medium" gutterBottom>
              Confirmação Enviada!
            </Typography>
            <Typography variant="body2">
              {multiple ? (
                <>
                  Cada participante receberá um e-mail com o ingresso e o QR Code no endereço cadastrado.
                </>
              ) : (
                <>
                  Um e-mail com todos os detalhes da sua {t('inscrição')} foi enviado para{' '}
                  <strong>{participant?.email}</strong>
                </>
              )}
            </Typography>
          </Alert>

          {showPlatformApp && (
          <Paper
            elevation={2}
            sx={{
              p: 3,
              mb: 3,
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              color: 'white',
              textAlign: 'center',
            }}
          >
            <Typography variant="h6" fontWeight="bold" gutterBottom>
              📱 Baixe o App Meu Sacramento
            </Typography>
            <Typography variant="body2" sx={{ mb: 2, opacity: 0.95 }}>
              Tenha acesso rápido a horários de missas, confissões e eventos católicos perto de você!
            </Typography>
            <Button
              variant="contained"
              size="large"
              href="https://linktr.ee/meusacramento?utm_source=linktree_profile_share&ltsid=dd32c04c-4bae-4b31-8b37-58c34d580735"
              target="_blank"
              rel="noopener noreferrer"
              sx={{
                bgcolor: 'white',
                color: '#667eea',
                fontWeight: 'bold',
                '&:hover': {
                  bgcolor: '#f5f5f5',
                  transform: 'translateY(-2px)',
                  boxShadow: '0 6px 20px rgba(0,0,0,0.2)',
                },
                transition: 'all 0.3s ease',
              }}
            >
              Baixar Aplicativo
            </Button>
          </Paper>
          )}

          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
            <Button component={Link} to="/" variant="contained" size="large" sx={{ minWidth: 200 }}>
              Voltar aos Eventos
            </Button>
            <Button
              variant="outlined"
              size="large"
              onClick={() => current?.registrationId && onDownloadTicket(current.registrationId)}
              sx={{ minWidth: 200 }}
            >
              {multiple
                ? `Baixar ingresso — ${firstName(participant?.name || 'Pessoa')}`
                : 'Baixar Ingresso'}
            </Button>
          </Box>
        </Box>
      </Paper>
    </Container>
  );
};

export default RegistrationSuccessView;
